import { Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import {
  Initiative,
  InitiativeSource,
  InitiativeStatus,
  KPIOverviewData,
} from '../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../api/projects-client/projects-client.type';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { icons } from '../../../../assets/icons/icons';
import { SortDirection } from '../../../../containers/strategy/initiative-performance/initiative-performance.type';
import { downloadCSV } from '../../../../helpers/csv-download';
import { formatCurrency } from '../../../../helpers/currency-helpers';
import { toUTCDate } from '../../../../helpers/date-time-helpers';
import { setTableSortBy, setTableSortDirection } from '../../../../store/strategy-store/strategy-store.actions';
import { TreeMapMetric } from '../../../../store/strategy-store/strategy-store.type';
import { primaryBase, redBase, skyBase, yellowBase } from '../../../../styles/design-tokens';
import { TableColumn } from '../../../data-management/components/table';
import { iconsIntegrations } from '../../../integrations/assets';
import { InitiativeRecord } from '../../strategy.type';
import { CompletionCell, EndDateCell, InitiativeIDCell } from './main-section-cell';
import { ParentOutcomesCell } from './parent-outcomes-cell';

/**
 * Gets the table column configuration for the initiatives table
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {Outcome[]} outcomes - Array of outcomes for displaying parent outcomes
 * @param {boolean} showParentOutcomes - Whether to show parent outcomes column (based on feature flag)
 * @param {string} currencyCode - Currency code for cost formatting (default 'USD')
 * @returns {TableColumn<Initiative>[]} Array of column configurations
 */
const getTableColumns = (
  t: TFunction,
  outcomes: Outcome[] = [],
  showParentOutcomes = false,
  currencyCode = 'USD',
): TableColumn<InitiativeRecord>[] => {
  const columns: TableColumn<InitiativeRecord>[] = [
    {
      key: 'key',
      label: t('strategy.initiatives.table.initiative_id'),
      width: '10%',
      sortable: true,
      render: (_, initiative) => <InitiativeIDCell initiative={initiative} />,
    },
    {
      key: 'name',
      label: t('strategy.initiatives.table.name'),
      width: '40%',
      sortable: true,
      render: (_, initiative) => <Text size="small">{initiative.name}</Text>,
    },
    {
      key: 'percent_progress',
      label: t('strategy.initiatives.table.completion'),
      width: '10%',
      sortable: true,
      render: (_, initiative) => <CompletionCell initiative={initiative} />,
    },
    {
      key: 'end_date',
      label: t('strategy.initiatives.table.target_end'),
      width: '10%',
      sortable: true,
      render: (_, initiative) => <EndDateCell initiative={initiative} />,
    },
    {
      key: 'costs_to_date',
      label: t('strategy.initiatives.table.cost_to_date'),
      width: '10%',
      sortable: true,
      render: (_, initiative) => (
        <Text size="small" style={{ whiteSpace: 'nowrap' }}>
          {formatCurrency(initiative.costs_to_date?.initiative, { currency: currencyCode })}
        </Text>
      ),
    },
    {
      key: 'contributing_teams_label',
      label: t('strategy.initiatives.table.teams'),
      width: '10%',
      sortable: true,
      render: (_, initiative) => <Text size="small">{initiative.contributing_teams_label}</Text>,
    },
  ];

  // Add parent outcomes column if feature flag is enabled
  if (showParentOutcomes) {
    columns.push({
      key: 'parent_outcomes' as keyof InitiativeRecord,
      label: t('strategy.initiatives.table.parent_outcomes'),
      width: '12%',
      sortable: true,
      render: (_, initiative) => <ParentOutcomesCell initiative={initiative} outcomes={outcomes} />,
    });
  }

  return columns;
};

/**
 * Creates a sort function with access to outcomes data via closure
 *
 * @param {Outcome[]} outcomes - Array of outcomes for parent outcome lookups
 * @returns A sort function that can properly sort by parent outcomes
 */
const createSortTableWithOutcomes = (outcomes: Outcome[] = []) => {
  return (data: InitiativeRecord[], sortBy: keyof InitiativeRecord, direction: 'asc' | 'desc'): InitiativeRecord[] => {
    setTableSortBy(sortBy);
    setTableSortDirection(direction as SortDirection);
    return customSortWithOutcomes(data, sortBy, direction, outcomes);
  };
};

/**
 * Natural sort comparison for strings with embedded numbers (e.g., "goal-2" before "goal-10")
 *
 * @param {string} a - First string to compare
 * @param {string} b - Second string to compare
 * @returns {number} Comparison result (-1, 0, or 1)
 */
const naturalSort = (a: string, b: string): number => {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Custom sort function for initiatives data with outcomes lookup support
 *
 * @param {InitiativeRecord[]} data - The array of initiatives to sort
 * @param {keyof InitiativeRecord} sortBy - The field to sort by
 * @param {'asc' | 'desc'} direction - The sort direction
 * @param {Outcome[]} outcomes - Array of outcomes for parent outcome lookups
 * @returns {InitiativeRecord[]} A new sorted array of initiatives
 */
const customSortWithOutcomes = (
  data: InitiativeRecord[],
  sortBy: keyof InitiativeRecord,
  direction: 'asc' | 'desc',
  outcomes: Outcome[] = [],
): InitiativeRecord[] => {
  return [...data].sort((a, b) => {
    const multiplier = direction === 'asc' ? 1 : -1;

    switch (sortBy) {
      case 'key':
        return multiplier * naturalSort(String(a.key || ''), String(b.key || ''));

      case 'name':
        return multiplier * naturalSort(String(a.name || ''), String(b.name || ''));

      case 'percent_progress': {
        const aProgress = a.percent_progress || 0;
        const bProgress = b.percent_progress || 0;
        return multiplier * (aProgress - bProgress);
      }

      case 'end_date': {
        const aDate = a.end_date ? new Date(a.end_date).getTime() : 0;
        const bDate = b.end_date ? new Date(b.end_date).getTime() : 0;
        return multiplier * (aDate - bDate);
      }

      case 'costs_to_date': {
        const aCost = a.costs_to_date?.initiative || 0;
        const bCost = b.costs_to_date?.initiative || 0;
        return multiplier * (aCost - bCost);
      }

      case 'parent_outcomes' as keyof InitiativeRecord: {
        const aParentOutcomes = outcomes.filter((outcome) => outcome.initiatives?.some((init) => init.id === a.id));
        const bParentOutcomes = outcomes.filter((outcome) => outcome.initiatives?.some((init) => init.id === b.id));

        const aFirstKey = aParentOutcomes[0]?.key || '';
        const bFirstKey = bParentOutcomes[0]?.key || '';

        return multiplier * naturalSort(String(aFirstKey), String(bFirstKey));
      }

      case 'contributing_teams_label':
        return multiplier * naturalSort(a.contributing_teams_label, b.contributing_teams_label);

      default:
        return 0;
    }
  });
};

/**
 * Handles downloading a CSV file for the initiatives table
 *
 * @param initiatives - The initiatives to download
 * @param teams - The teams to include in the download
 * @param t - Translation function from `useTranslation()`
 * @param outcomes - The outcomes for parent outcome lookup
 * @param showParentOutcomes - Whether to include parent outcomes in the export
 * @returns {void}
 */
const handleDownloadCSV = (
  initiatives: Initiative[],
  teams: Team[],
  t: TFunction,
  outcomes: Outcome[] = [],
  showParentOutcomes = false,
): void => {
  try {
    const headers = [
      t('strategy.initiatives.table.initiative_id'),
      t('strategy.initiatives.table.name'),
      t('strategy.initiatives.labels.status_by_points', { estimation: t('common.estimation') }),
      t('strategy.initiatives.labels.status_by_tasks', { work_units: t('common.work_units') }),
      t('strategy.initiatives.table.completion'),
      t('strategy.initiatives.table.target_end'),
      t('strategy.initiatives.table.cost_to_date'),
    ];

    // Add parent outcomes header if feature flag is enabled
    if (showParentOutcomes) {
      headers.push(t('strategy.initiatives.table.parent_outcomes'));
    }

    headers.push(t('strategy.initiatives.table.teams'));

    const noData = t('strategy.initiatives.labels.no_data');

    const rows = initiatives.map((initiative) => {
      const row = [
        initiative.key || noData,
        initiative.name,
        initiative.status?.by_points?.status || noData,
        initiative.status?.by_tasks?.status || noData,
        initiative.percent_progress || 0,
        initiative.end_date ? toUTCDate(initiative.end_date).format('MMM D, YYYY') : noData,
        initiative.costs_to_date?.initiative || 0,
      ];

      // Add parent outcomes if feature flag is enabled
      if (showParentOutcomes) {
        const parentOutcomes = outcomes.filter((outcome) =>
          outcome.initiatives?.some((init) => init.id === initiative.id),
        );
        const outcomeKeys = parentOutcomes.map((outcome) => outcome.key).join(', ');
        row.push(outcomeKeys || noData);
      }

      row.push(getContributingTeams(initiative, teams) || noData);

      return row;
    });

    downloadCSV([headers, ...rows], 'initiatives.csv');
  } catch (error) {
    console.error('Error downloading CSV', error);
  }
};

/**
 * Returns the appropriate color for an initiative status
 *
 * @param {InitiativeStatus} status - The status of the initiative
 * @returns {string} The color code corresponding to the status
 */
const getStatusColor = (status: InitiativeStatus): string => {
  switch (status) {
    case InitiativeStatus.OnTrack:
      return primaryBase;
    case InitiativeStatus.AtRisk:
      return yellowBase;
    case InitiativeStatus.OffTrack:
      return redBase;
    case InitiativeStatus.Unknown:
      return skyBase;
    default:
      return skyBase;
  }
};

/**
 * Returns the appropriate icon for an initiative source
 *
 * @param {InitiativeSource} source - The source of the initiative
 * @returns {string} The icon path corresponding to the source
 */
const getSourceIcon = (source: InitiativeSource): string => {
  const sourceIcons = {
    [InitiativeSource.Bloomfilter]: icons.iconBloomfilter,
    [InitiativeSource.Jira]: iconsIntegrations.iconJiraCloud,
    [InitiativeSource.JDC]: iconsIntegrations.iconJiraDataCenter,
    [InitiativeSource.ADO]: iconsIntegrations.iconADO,
    [InitiativeSource.Github]: icons.iconGithub,
  };

  return sourceIcons[source];
};

/**
 * Gets a comma-separated string of contributing team names for an initiative
 *
 * @param {Initiative} initiative - The initiative to get contributing teams for
 * @param {Team[]} teams - Array of all available teams
 * @returns {string} Comma-separated list of team names that contribute to the initiative
 */
const getContributingTeams = (initiative: Initiative, teams: Team[]): string => {
  const teamIds = Object.keys(initiative.contributing_teams_over_time || {});
  const teamNames = teamIds.map((id) => teams.find((team) => team.id === id)?.name).filter(Boolean);

  return teamNames.join(', ');
};

/**
 * Gets a comma-separated string of contributing board names for an initiative
 *
 * @param {Initiative} initiative - The initiative to get contributing boards for
 * @param {Team[]} teams - Array of all available teams
 * @returns {string} Comma-separated list of board names that contribute to the initiative
 */
const getContributingBoards = (initiative: Initiative, teams: Team[]): string => {
  const boardIds = Object.keys(initiative.contributing_boards_over_time || {});
  const boardNames = boardIds
    .map((id) => teams.flatMap((team) => team.subprojects).find((board) => board.id === id)?.name)
    .filter(Boolean);

  return boardNames.join(', ');
};

/**
 * Returns the available metric options for the tree map visualization
 *
 * @param {KPIOverviewData | null} kpiOverviewData - KPI data used to gate options
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {{ label: string; value: TreeMapMetric }[]} Array of metric options with labels and values
 */
const getTreeMapMetricOptions = (
  kpiOverviewData: KPIOverviewData | null,
  t: TFunction,
): { label: string; value: TreeMapMetric }[] => {
  const mapOptions = [];
  if (Object.keys(kpiOverviewData?.initiative_cost_breakdown || {}).length > 0) {
    mapOptions.push({
      value: TreeMapMetric.CostByInitiative,
      label: t('strategy.initiatives.metrics.cost_by_initiative'),
    });
  }
  if (Object.keys(kpiOverviewData?.initiative_effort_breakdown || {}).length > 0) {
    mapOptions.push({
      value: TreeMapMetric.EffortByInitiative,
      label: t('strategy.initiatives.metrics.effort_by_initiative'),
    });
  }
  return mapOptions;
};

export {
  createSortTableWithOutcomes,
  getContributingBoards,
  getContributingTeams,
  getSourceIcon,
  getStatusColor,
  getTableColumns,
  getTreeMapMetricOptions,
  handleDownloadCSV,
};
